import { Form, Formik } from "formik";
import type { WirelessSurveyDataCollectorOut } from "kubb";
import type { WirelessSurveyOut } from "kubb";
import type { WirelessSurveySamplePointOutput } from "kubb";
import type { WirelessSurveyTarget } from "kubb";
import { useState } from "react";
import DataTable, {
  type ConditionalStyles,
  type TableColumn,
} from "react-data-table-component";
import tailwindConfig from "tailwind-config";
import resolveConfig from "tailwindcss/resolveConfig";
import AutoSubmit from "../../../components/Forms/AutoSubmit";
import { ButtonGroupField } from "../../../components/Forms/ButtonGroup";
import { Col, Container } from "../../../components/Theme/grid.tsx";
import CSVExportButton from "../../../components/utils/CSVExport/CSVExportButton";
import type {
  WirelessDataSurveyMap,
  WirelessDataSurveyMapItem,
  WirelessSurveyDataPoint,
} from "../../../model/assets/wirelessSurveyData";

const fullConfig = resolveConfig(tailwindConfig);

export type WirelessSurveyFoundEnum =
  | "foundInsideSamplePoint"
  | "never"
  | "foundOutsideSamplePoint";

export interface WirelessSurveySummaryTableData {
  serial_number: string;
  data: WirelessDataSurveyMapItem;
  found: WirelessSurveyFoundEnum;
  target?: WirelessSurveyTarget;
  sortOrder?: number;
}

function compareTableData(
  a: WirelessSurveySummaryTableData,
  b: WirelessSurveySummaryTableData,
): number {
  if ((a.sortOrder || 0) > (b.sortOrder || 0)) {
    return 1;
  }
  if ((a.sortOrder || 0) < (b.sortOrder || 0)) {
    return -1;
  }
  return 0;
}

function toSortNumber(
  serial_number: string,
  maxSortOrder: number,
  target?: WirelessSurveyTarget,
): number {
  return target?.sort_order
    ? target.sort_order
    : target !== undefined
      ? maxSortOrder + Number.parseInt(serial_number) / 100000000
      : maxSortOrder + 1 + Number.parseInt(serial_number) / 100000000;
}

export function propsToTableData(
  surveyMap: WirelessDataSurveyMap,
  filter: string,
  serial_numberTargetsMap: { [serial_number: string]: WirelessSurveyTarget },
): WirelessSurveySummaryTableData[] {
  const surveyMapCopy = new Map([...surveyMap.entries()]);
  let maxSortOrder = 0;
  if (
    filter === "targets" ||
    filter === "all" ||
    filter === "targets_not_in_sp"
  ) {
    /* add targets that have not been received */
    const receivedSerials: string[] = [...surveyMap.entries()].map(
      (v) => v[1].serial_number,
    );

    for (const [serial, target] of Object.entries(serial_numberTargetsMap)) {
      if (receivedSerials.indexOf(serial) === -1) {
        surveyMapCopy.set(serial, {
          serial_number: serial,
          manufacturer: "",
          version: "",
          medium: "",
          dataCollectorDataMap: new Map(),
          dataCollectorSamplePointMap: new Map(),
        });
      }
      if (target.sort_order && target.sort_order > maxSortOrder) {
        maxSortOrder = target.sort_order;
      }
    }
  }

  return [...surveyMapCopy.entries()]
    .map((v) => {
      return {
        serial_number: v[0],
        data: v[1],
        found: isDataReceived(
          v[1].dataCollectorDataMap,
          v[1].dataCollectorSamplePointMap,
        ),
        target: serial_numberTargetsMap[v[1].serial_number],
        sortOrder: toSortNumber(
          v[1].serial_number,
          maxSortOrder,
          serial_numberTargetsMap[v[1].serial_number],
        ),
      } as WirelessSurveySummaryTableData;
    })
    .sort(compareTableData)
    .filter((v) => {
      switch (filter) {
        case "received":
          return v.found !== "never";
        case "targets_not_in_sp":
          return (
            serial_numberTargetsMap[v.data.serial_number] !== undefined &&
            v.found !== "foundInsideSamplePoint"
          );
        case "targets":
          return serial_numberTargetsMap[v.data.serial_number] !== undefined;
        default:
          return true;
      }
    });
}

function isDataReceived(
  data: Map<string, WirelessSurveyDataPoint[]>,
  samplePointData: Map<string, WirelessSurveyDataPoint[]>,
): WirelessSurveyFoundEnum {
  if (samplePointData.size !== 0) {
    const filtered = [...samplePointData.entries()].filter(([k, _]) => {
      return k.split("-").at(-1) !== "NONE";
    });
    for (const [_, v] of filtered) {
      if (v[0].sampleIsIncluded) {
        return "foundInsideSamplePoint";
      }
    }

    return "foundOutsideSamplePoint";
  } else {
    return data.size !== 0 ? "foundOutsideSamplePoint" : "never";
  }
}

function meanRssi(data: WirelessSurveyDataPoint[]) {
  return Math.round(
    data.reduce((p, c) => p + Number.parseInt(c.rssi), 0) / data.length,
  );
}

function samplePointRow(
  dataCollector: WirelessSurveyDataCollectorOut,
  samplePoint?: WirelessSurveySamplePointOutput,
) {
  return {
    name: (
      <div className={"container px-0"}>
        <div>{samplePoint?.sample_point_name || "-"}</div>
        <div>
          <small>
            ({dataCollector.data_collector_name} {dataCollector.serial_number})
          </small>
        </div>
      </div>
    ),
    cell: (row: WirelessSurveySummaryTableData) => {
      const data =
        row.data.dataCollectorSamplePointMap.get(
          `${dataCollector.data_collector_id}-${samplePoint?.sample_point_name || "NONE"}`,
        ) || [];
      const rssi = meanRssi(data);
      return (
        <>
          {data?.length > 0 ? (
            <>
              {data.length}&nbsp;&nbsp;<small>({rssi})</small>
            </>
          ) : (
            <>-</>
          )}
        </>
      );
    },
  };
}

type columnOrderEnum = "collectorTime" | "name";

export default function WirelessSurveySummaryTable(props: {
  survey: WirelessSurveyOut;
  dataCollectors: WirelessSurveyDataCollectorOut[];
  wirelessDataSurveyMappedData: WirelessDataSurveyMap;
  targets?: WirelessSurveyTarget[];
}) {
  const haveTargets = props.targets && props.targets.length > 0;

  const [filter, setFilter] = useState<string>("all");
  const [showDataCollectorPoints, setShowDataCollectorPoints] =
    useState<boolean>(true);
  const [columnOrder, setColumnOrder] =
    useState<columnOrderEnum>("collectorTime");

  const serial_numberTargetsMap: {
    [serial_number: string]: WirelessSurveyTarget;
  } =
    props.targets?.reduce((c, v) => {
      return { ...c, [v.serial_number]: v };
    }, {}) || {};

  const data: WirelessSurveySummaryTableData[] = propsToTableData(
    props.wirelessDataSurveyMappedData,
    filter,
    serial_numberTargetsMap,
  );

  type targetData = [
    WirelessSurveyDataCollectorOut,
    WirelessSurveySamplePointOutput | undefined,
  ];

  function sortFunction(a: targetData, b: targetData) {
    let [aValue, bValue]: [string, string] = ["", ""];
    if (columnOrder === "collectorTime") {
      aValue = `${a[0].data_collector_name}-${a[1]?.start_date_time}`;
      bValue = `${b[0].data_collector_name}-${b[1]?.start_date_time}`;
    } else if (columnOrder === "name") {
      aValue = a[1]?.sample_point_name || a[0].data_collector_name;
      bValue = b[1]?.sample_point_name || a[0].data_collector_name;
    } else {
      throw new Error("Unknown columnOrder");
    }
    if (aValue > bValue) {
      return 1;
    } else if (aValue < bValue) {
      return -1;
    } else {
      return 0;
    }
  }

  const targets: targetData[] = (props.dataCollectors || [])
    .flatMap((dataCollector) => {
      return [
        ...(dataCollector.sample_points || []).map(
          (samplePoint) => [dataCollector, samplePoint] as targetData,
        ),
        showDataCollectorPoints
          ? ([dataCollector, undefined] as targetData)
          : undefined,
      ];
    })
    .filter((v) => v !== undefined)
    .sort(sortFunction);

  const samplePointColumns: TableColumn<WirelessSurveySummaryTableData>[] =
    targets.map(([dataCollector, samplePoint]) =>
      samplePointRow(dataCollector, samplePoint),
    );

  const columns: TableColumn<WirelessSurveySummaryTableData>[] = [
    {
      name: "Target ID",
      compact: true,
      selector: (row) => row.sortOrder || 0,
      cell: (row) => row.target?.sort_order || "-",
      omit: !haveTargets,
      sortable: true,
    },
    {
      name: "Label",
      compact: true,
      selector: (row) => row.target?.label || "",
      omit: !haveTargets,
    },
    {
      name: "Serial",
      compact: true,
      selector: (row) => row.data.serial_number,
      sortable: true,
    },
    {
      name: "Manufacturer",
      compact: true,
      selector: (row) => row.data.manufacturer,
      sortable: true,
    },
    { name: "Medium", compact: true, selector: (row) => row.data.medium },
    { name: "Version", compact: true, selector: (row) => row.data.version },
    ...samplePointColumns,
  ];
  const conditionalRowStyles: ConditionalStyles<WirelessSurveySummaryTableData>[] =
    props.targets
      ? [
          {
            when: (row: WirelessSurveySummaryTableData) =>
              serial_numberTargetsMap[row.data.serial_number] != null &&
              row.found === "foundInsideSamplePoint",
            style: {
              color: fullConfig.theme.colors.gray["200"],
              backgroundColor: fullConfig.theme.colors.success,
            },
          },
          {
            when: (row: WirelessSurveySummaryTableData) =>
              serial_numberTargetsMap[row.data.serial_number] != null &&
              row.found === "foundOutsideSamplePoint",
            style: {
              color: fullConfig.theme.colors.gray["200"],
              backgroundColor: fullConfig.theme.colors.warning,
            },
          },
          {
            when: (row: WirelessSurveySummaryTableData) =>
              row.found === "never",
            style: {
              color: fullConfig.theme.colors.gray["200"],
              backgroundColor: fullConfig.theme.colors.danger,
            },
          },
        ]
      : [];
  // const dataCollectors = (props.dataCollectors || []).flatMap(
  //   (dataCollector) => {
  //     return [
  //       ...(dataCollector.sample_points || []).map(
  //         (samplePoint) =>
  //           `${dataCollector.data_collector_id}-${samplePoint.sample_point_name}`,
  //       ),
  //       `${dataCollector.data_collector_id}-NONE`,
  //     ];
  //   },
  // );
  return (
    <>
      {haveTargets && (
        <Formik
          initialValues={{
            targets: filter,
            dataCollectors: showDataCollectorPoints,
            columnOrder,
          }}
          onSubmit={(values) => {
            setFilter(values.targets);
            setShowDataCollectorPoints(values.dataCollectors);
            setColumnOrder(values.columnOrder);
          }}
          enableReinitialize={true}
        >
          {() => (
            <>
              <AutoSubmit />
              <Form>
                <Container>
                  <Col sm={12} md={6}>
                    <ButtonGroupField
                      fullWidth={true}
                      fieldName={"targets"}
                      label={{ label: "Filter" }}
                      options={[
                        { value: "all", label: "Both" },
                        { value: "targets", label: "Targets" },
                        {
                          value: "targets_not_in_sp",
                          label: "Targets not in sample point",
                        },
                        { value: "received", label: "Received" },
                      ]}
                    />
                  </Col>
                  <Col sm={12} md={3}>
                    <ButtonGroupField
                      fullWidth={true}
                      fieldName={"dataCollectors"}
                      label={{ label: "Show non-sample-point data" }}
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                    />
                  </Col>
                  <Col sm={12} md={3}>
                    <ButtonGroupField
                      fullWidth={true}
                      fieldName={"columnOrder"}
                      label={{ label: "Column order" }}
                      options={[
                        {
                          value: "collectorTime",
                          label: "Data Collector - Time",
                        },
                        { value: "name", label: "Sample Point Name" },
                      ]}
                    />
                  </Col>
                </Container>
              </Form>
            </>
          )}
        </Formik>
      )}
      <CSVExportButton
        download={() => {
          return Promise.resolve(
            data.map((v) => {
              return {
                target_label: v.target?.label,
                target_serial_number: v.target?.serial_number,
                target_sort_order: v.target?.sort_order || 0,
                serial_number: v.data.serial_number,
                manufacturer: v.data.manufacturer,
                medium: v.data.medium,
                version: v.data.version,
                ...Object.fromEntries(
                  targets.flatMap(([dataCollector, samplePoint]) => {
                    const key = samplePoint
                      ? `${dataCollector.data_collector_id}-${samplePoint.sample_point_name}`
                      : `${dataCollector.data_collector_id} - NONE`;
                    const name = samplePoint
                      ? `${samplePoint.sample_point_name} - (${dataCollector.data_collector_name})`
                      : `NONE - (${dataCollector.data_collector_name})`;
                    return [
                      [
                        `${name}-count`,
                        v.data.dataCollectorSamplePointMap.get(key)?.length ||
                          0,
                      ],
                      [
                        `${name}-rssi`,
                        meanRssi(
                          v.data.dataCollectorSamplePointMap.get(key) || [],
                        ) || "",
                      ],
                    ];
                  }),
                ),
              };
            }),
          );
        }}
        columnOrder={[
          "target_label",
          "target_serial_number",
          "target_sort_order",
          "serial_number",
          "manufacturer",
          "medium",
          "version",
        ]}
        filename={`wireless_survey_${props.survey.survey_name}_summary.csv`}
      />
      <DataTable
        columns={columns}
        data={data}
        dense={true}
        pagination={true}
        paginationPerPage={100}
        paginationRowsPerPageOptions={[25, 50, 100, 250, 1000]}
        // fixedHeader={true}
        conditionalRowStyles={conditionalRowStyles}
      />
    </>
  );
}
